import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePageMeta } from '../hooks/usePageMeta';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';

/** Routes that must not appear in Google (account, auth, checkout, sell). */
function isNoIndexPath(pathname: string): boolean {
    const p = pathname.toLowerCase();
    return (
        p.startsWith('/admin') ||
        p.startsWith('/my-account') ||
        p.startsWith('/account-centre') ||
        p.startsWith('/account-status') ||
        p.startsWith('/checkout') ||
        p.startsWith('/sell') ||
        p.startsWith('/edit-product') ||
        p.startsWith('/favourites') ||
        p.startsWith('/helpdesk') ||
        p.startsWith('/my-petito') ||
        p.startsWith('/my-rebaby') ||
        p.startsWith('/login') ||
        p.startsWith('/register') ||
        p.startsWith('/check-email') ||
        p.startsWith('/confirm-email') ||
        p.startsWith('/forgot-password') ||
        p.startsWith('/reset-password') ||
        p.startsWith('/orders') ||
        p === '/sales' ||
        p.startsWith('/sales/')
    );
}

/**
 * Maps shop routes to tab titles: "{Page} - Petito".
 * Pages that call useDocumentTitle themselves (Home, Login, ProductDetail) take precedence when mounted after this.
 * Also sets robots noindex on private routes and a path-level canonical for public pages.
 */
const ShopDocumentTitle = () => {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);

    const page = useMemo(() => {
        const p = pathname.toLowerCase();
        if (p === '/' || p === '/home') return tr('page.home.title', 'Home');
        // Product detail sets a richer title once data loads
        if (p.startsWith('/products/')) return tr('page.product.title', 'Product');
        if (p.startsWith('/search')) return tr('page.search.title', 'Search');
        if (p.startsWith('/login')) return tr('page.login.title', 'Log in');
        if (p.startsWith('/register')) return tr('page.register.title', 'Register');
        if (p.startsWith('/my-account/listings')) return tr('page.listings.title', 'My listings');
        if (p.startsWith('/my-account/orders')) return tr('page.orders.title', 'Orders');
        if (p.startsWith('/my-account')) return tr('page.account.title', 'Account');
        if (p.startsWith('/account-centre')) return tr('page.accountcentre.title', 'Account centre');
        if (p.startsWith('/favourites')) return tr('page.favourites.title', 'Favourites');
        if (p.startsWith('/sell') || p.startsWith('/edit-product')) return tr('page.sell.title', 'Sell');
        if (p.startsWith('/my-petito') || p.startsWith('/my-rebaby')) return tr('page.myrebaby.title', 'My Journey');
        if (p.startsWith('/helpdesk')) return tr('page.helpdesk.title', 'Helpdesk');
        if (p.startsWith('/about')) return tr('page.about.title', 'About');
        if (p.startsWith('/how-it-works')) return tr('page.howitworks.title', 'How it works');
        if (p.startsWith('/faq')) return tr('page.faq.title', 'FAQ');
        if (p.startsWith('/privacy')) return tr('page.privacy.title', 'Privacy');
        if (p.startsWith('/terms')) return tr('page.terms.title', 'Terms');
        if (p.startsWith('/rules')) return tr('page.rules.title', 'Community rules');
        if (p.startsWith('/shipping')) return tr('page.shipping.title', 'Shipping');
        if (p.startsWith('/partner')) return tr('page.partner.title', 'Partner');
        if (p.startsWith('/blog')) return tr('page.blog.title', 'Blog');
        if (p.startsWith('/users/')) return tr('page.profile.title', 'Profile');
        if (p.startsWith('/forgot-password')) return tr('page.forgot.title', 'Forgot password');
        if (p.startsWith('/reset-password')) return tr('page.reset.title', 'Reset password');
        if (p.startsWith('/checkout')) return tr('page.checkout.title', 'Checkout');
        return '';
    }, [pathname, t]);

    const noIndex = isNoIndexPath(pathname);
    // ProductDetail owns meta once loaded; skip generic product path meta here
    const isProduct = pathname.toLowerCase().startsWith('/products/');

    useDocumentTitle(page);
    usePageMeta({
        noIndex,
        path: noIndex || isProduct ? null : pathname === '/' ? '/home' : pathname,
    });

    return null;
};

export default ShopDocumentTitle;
